import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { HeaderPortal, useSetHeaderRecordName } from "../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import { toast } from "../../components/ui-components/Toast.jsx";
import { getBoardReport, listBoardReportRuns, createRun } from "./document/boardReportApi.js";
import OverviewPanel from "./panels/OverviewPanel.jsx";
import RunsPanel from "./panels/RunsPanel.jsx";
import SettingsPanel from "./panels/SettingsPanel.jsx";
import PreventiveActionsPanel from "./panels/PreventiveActionsPanel.jsx";

const TAB_IDS = ["overview", "runs", "settings", "actions"];

/**
 * BoardReportHome - the single home for one board report (/board-reports/:id), replacing the
 * scattered /config, /template and /runs routes. Owns the report + run data and the "New run" action
 * so every tab shares one source of truth; the active tab is URL-driven (?tab=) like ForecastLabPage,
 * so tab deep-links and the old-route redirects (see index.js) both work. Tabs:
 *   overview  - status at a glance + primary next steps
 *   runs      - version history + New run (RunsPanel)
 *   settings  - details + delivery: recipients + schedule (SettingsPanel)
 *   actions   - preventive-actions authoring (PreventiveActionsPanel); only shown for
 *               budget_variance_risk reports (report.type), since other report types have
 *               no such surface
 * Editing the template (block layout/bindings) is an admin task on its own screen
 * (/board-reports/:id/template, BoardReportTemplateAdmin behind AdminRoute), not a tab here.
 */
const BoardReportHome = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [report, setReport] = useState(null);
  const [runs, setRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [creating, setCreating] = useState(false);

  // The Settings tab owns the report form + its save logic; the header hosts the
  // "Save changes" primary action for that tab. We reach into SettingsPanel via a
  // ref (save()) and mirror its saving state so the header button can show
  // "Saving…" / disable while a save is in flight.
  const settingsRef = useRef(null);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // The Actions tab (preventive-actions authoring) only applies to budget_variance_risk
  // reports — other report types have no such surface. Gate both the visible tab list and
  // the ?tab= resolution on it so a stale/guessed ?tab=actions link falls back to overview
  // instead of rendering a tab that isn't offered.
  const showActions = report?.type === "budget_variance_risk";
  const rawTab = searchParams.get("tab");
  const allowedTabIds = showActions ? TAB_IDS : TAB_IDS.filter((t) => t !== "actions");
  const activeTab = allowedTabIds.includes(rawTab) ? rawTab : "overview";
  const setTab = useCallback(
    (tab) => {
      const next = new URLSearchParams(searchParams);
      if (tab === "overview") next.delete("tab");
      else next.set("tab", tab);
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setLoadError(null);
      const [reportData, runsData] = await Promise.all([getBoardReport(id), listBoardReportRuns(id)]);
      setReport(reportData);
      setRuns(Array.isArray(runsData) ? runsData : []);
    } catch (err) {
      setLoadError(err.message || "Failed to load board report");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleNewRun = useCallback(async () => {
    setCreating(true);
    try {
      const run = await createRun(id);
      navigate(`/board-reports/${id}/runs/${run.id}/review`);
    } catch (err) {
      toast.error("Failed to create run");
      setCreating(false);
    }
  }, [id, navigate]);

  // Feed the global TopBar: the report name becomes the breadcrumb tail + the
  // page title, so this detail page reads like the other record pages (budgets,
  // investigations, …) rather than carrying its own header bar.
  useSetHeaderRecordName(report?.name || null);

  const tabs = useMemo(() => {
    const base = [
      { id: "overview", label: "Overview" },
      { id: "runs", label: "Runs", count: runs.length },
      { id: "settings", label: "Settings" },
    ];
    if (showActions) base.push({ id: "actions", label: "Actions" });
    return base;
  }, [runs.length, showActions]);

  // Primary action lives in the app header (TopBar actions slot), contextual to
  // the active tab — New run on overview/runs, Save changes on settings — so every
  // tab's primary action sits in one place, next to Ask (like "New budget" on the
  // budgets page). Hidden while the report failed to load, since there's nothing
  // to act on.
  let headerAction = null;
  if (!loadError) {
    if (activeTab === "overview" || activeTab === "runs") {
      headerAction = (
        <button type="button" className={primaryButtonCls} onClick={handleNewRun} disabled={creating}>
          {creating ? "Creating…" : "New run"}
        </button>
      );
    } else if (activeTab === "settings") {
      headerAction = (
        <button
          type="button"
          className={primaryButtonCls}
          onClick={() => settingsRef.current?.save()}
          disabled={isLoading || settingsSaving}
        >
          {settingsSaving ? "Saving…" : "Save changes"}
        </button>
      );
    }
  }

  return (
    <div className="h-full flex flex-col bg-background-grey">
      {/* Tabs render in the TopBar's row-2 strip (like the budgets status tabs);
          the primary action sits in the row-1 actions cluster next to Ask. */}
      <HeaderPortal slot="tabstrip">
        {tabs.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              aria-current={active ? "page" : undefined}
              style={{ height: "100%" }}
              className={`relative box-border inline-flex items-center border-none bg-transparent px-4 text-[13.5px] whitespace-nowrap transition-colors cursor-pointer focus-visible:outline-none ${
                active
                  ? "text-text font-semibold after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[3px] after:rounded-t after:bg-primary"
                  : "text-text-3 font-medium hover:text-text-2"
              }`}
            >
              {t.label}
              {t.count != null ? ` (${t.count})` : ""}
            </button>
          );
        })}
      </HeaderPortal>
      {headerAction && <HeaderPortal slot="actions">{headerAction}</HeaderPortal>}

      <div className="flex-1 overflow-auto p-8">
        {loadError ? (
          <div className="text-danger">{loadError}</div>
        ) : (
          <>
            {activeTab === "overview" && (
              <OverviewPanel
                id={id}
                report={report}
                runs={runs}
                isLoading={isLoading}
                onManageDelivery={() => setTab("settings")}
              />
            )}
            {activeTab === "runs" && (
              <RunsPanel
                id={id}
                reportName={report?.name}
                runs={runs}
                isLoading={isLoading}
                error={loadError}
                reload={reload}
                onNewRun={handleNewRun}
                creating={creating}
              />
            )}
            {activeTab === "settings" &&
              (isLoading ? (
                <div className="text-text-3">Loading…</div>
              ) : (
                <SettingsPanel
                  ref={settingsRef}
                  key={report?.id}
                  id={id}
                  report={report}
                  onSaved={reload}
                  onSavingChange={setSettingsSaving}
                />
              ))}
            {activeTab === "actions" && showActions && <PreventiveActionsPanel id={id} report={report} />}
          </>
        )}
      </div>
    </div>
  );
};

export default BoardReportHome;
